import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Col, Modal, Row, Skeleton, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { previewManualExceptionEmails, sendManualExceptionEmails } from 'src/app/shared/services/BackgroundService'
import {
  EManualExceptionNotificationReason,
  IManualExceptionNotificationPreview,
  IManualExceptionNotificationSend,
} from 'src/app/shared/models/manualExceptionNotification.model'
import './index.scss'

const REASON_KEY_PREFIX = 'QuickExceptionProcessing:manualNotification.reason.'

/**
 * Resolves the message for a blocked/nothing-to-send reason. The "still retrying" message gets a
 * variant with the attempt counter, so the user knows how far the automated process has got.
 */
const resolveReasonKey = (
  reason?: string | null,
  data?: Pick<IManualExceptionNotificationPreview, 'currentAttempt' | 'maxAttempts'> | null,
) => {
  const hasAttempts = data?.currentAttempt != null && data?.maxAttempts != null

  if (reason === EManualExceptionNotificationReason.JOB_STILL_RUNNING && hasAttempts) {
    return `${REASON_KEY_PREFIX}${reason}_WITH_ATTEMPTS`
  }

  return `${REASON_KEY_PREFIX}${reason}`
}

interface IManualNotificationModalProps {
  isOpen: boolean
  onClose: () => void
  api: any
}

const ManualNotificationModal: React.FC<IManualNotificationModalProps> = ({ isOpen, onClose, api }) => {
  const { t } = useTranslation()

  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false)
  const [isSending, setIsSending] = useState<boolean>(false)
  const [preview, setPreview] = useState<IManualExceptionNotificationPreview | null>(null)

  const loadPreview = useCallback(async () => {
    setIsLoadingPreview(true)
    try {
      const res = await previewManualExceptionEmails()
      if (res?.statusCode === STATUS_CODES.SUCCESS) {
        setPreview(res?.data)
      }
    } catch (error: any) {
      api.error({
        message: t('QuickExceptionProcessing:manualNotification.previewFailed'),
        placement: 'topRight',
      })
    } finally {
      setIsLoadingPreview(false)
    }
  }, [api, t])

  useEffect(() => {
    if (isOpen) {
      loadPreview()
    } else {
      setPreview(null)
    }
  }, [isOpen, loadPreview])

  const handleSend = async () => {
    setIsSending(true)
    try {
      const res = await sendManualExceptionEmails()
      if (res?.statusCode === STATUS_CODES.SUCCESS && res?.data?.success) {
        api.success({
          message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.MANUAL_EXCEPTION_NOTIFICATIONS_SENT}`),
          description: t('QuickExceptionProcessing:manualNotification.checkSentEmailLog'),
          placement: 'topRight',
        })
        onClose()
        return
      }

      // The send was refused: surface the reason returned by the service.
      const sendResult: IManualExceptionNotificationSend | undefined = res?.data
      api.warning({
        message: t(resolveReasonKey(sendResult?.blockedReason, sendResult), {
          current: sendResult?.currentAttempt,
          total: sendResult?.maxAttempts,
        }),
        placement: 'topRight',
      })
      onClose()
    } catch (error: any) {
      api.error({
        message: t('QuickExceptionProcessing:manualNotification.sendFailed'),
        placement: 'topRight',
      })
    } finally {
      setIsSending(false)
    }
  }

  const isBlocked = !!preview?.blockedReason
  const hasNothingToSend = !!preview?.nothingToSendReason
  const canSend = !isLoadingPreview && !isBlocked && !hasNothingToSend

  const renderSummaryItem = (label: string, value: React.ReactNode) => (
    <Col span={12} className='manual-notification-modal__summary-item'>
      <Typography.Text type='secondary'>{label}</Typography.Text>
      <Typography.Text strong>{value}</Typography.Text>
    </Col>
  )

  return (
    <Modal
      className='manual-notification-modal'
      centered
      keyboard={false}
      maskClosable={false}
      open={isOpen}
      onCancel={onClose}
      title={t('QuickExceptionProcessing:manualNotification.title')}
      footer={[
        <Button key='btn-cancel' size='middle' onClick={onClose} disabled={isSending}>
          {t('common:cancel')}
        </Button>,
        <Button
          key='btn-send'
          size='middle'
          type='primary'
          loading={isSending}
          disabled={!canSend || isSending}
          onClick={handleSend}
        >
          {t('QuickExceptionProcessing:manualNotification.confirmSend')}
        </Button>,
      ]}
    >
      {isLoadingPreview ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <React.Fragment>
          {isBlocked && (
            <Alert
              type='warning'
              showIcon
              className='manual-notification-modal__alert'
              message={t(resolveReasonKey(preview?.blockedReason, preview), {
                current: preview?.currentAttempt,
                total: preview?.maxAttempts,
              })}
            />
          )}

          {!isBlocked && hasNothingToSend && (
            <Alert
              type='info'
              showIcon
              className='manual-notification-modal__alert'
              message={t(resolveReasonKey(preview?.nothingToSendReason, preview))}
            />
          )}

          {!isBlocked && preview?.alreadySentToday && (
            <Alert
              type='warning'
              showIcon
              className='manual-notification-modal__alert'
              message={t('QuickExceptionProcessing:manualNotification.alreadySentToday')}
            />
          )}

          {!isBlocked && (
            <React.Fragment>
              <Row gutter={[16, 16]} className='manual-notification-modal__summary'>
                {renderSummaryItem(t('QuickExceptionProcessing:manualNotification.targetDate'), preview?.targetDate)}
                {renderSummaryItem(t('QuickExceptionProcessing:manualNotification.clients'), preview?.clientCount)}
                {renderSummaryItem(t('QuickExceptionProcessing:manualNotification.accounts'), preview?.accountCount)}
                {renderSummaryItem(
                  t('QuickExceptionProcessing:manualNotification.checkExceptions'),
                  preview?.checkExceptionCount,
                )}
                {renderSummaryItem(
                  t('QuickExceptionProcessing:manualNotification.achExceptions'),
                  preview?.achExceptionCount,
                )}
              </Row>

              <Typography.Text strong className='manual-notification-modal__recipients-title'>
                {t('QuickExceptionProcessing:manualNotification.recipients', {
                  count: preview?.recipients?.length || 0,
                })}
              </Typography.Text>
              <div className='manual-notification-modal__recipients'>
                {preview?.recipients?.map((recipient) => (
                  <Typography.Text key={recipient} className='manual-notification-modal__recipient'>
                    {recipient}
                  </Typography.Text>
                ))}
              </div>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Modal>
  )
}

export default ManualNotificationModal
